import React, { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ImageUploader } from "./../shared/ImageUploader";

// Shared survey screens — rendered by the runner (Show.jsx) and the
// editor's live preview (Preview.jsx). The runner passes real callbacks;
// the preview passes no-ops + the `preview` prop so inputs are disabled.

// ─── NPS / numeric strips (also used by the editor's per-question
// "how this looks to the respondent" inline preview) ───────────────────

export function NumericStrip({ selected, onChange, disabled }) {
  const handle = (n) => { if (!disabled && onChange) onChange([ String(n) ]); };
  return (
    <div className="grid grid-cols-10 gap-1">
      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => {
        const isSel = selected != null && String(n) === String(selected);
        return (
          <button key={n} type="button" disabled={disabled} onClick={() => handle(n)}
                  className={`aspect-square rounded border-[2px] text-sm font-bold transition-transform ${disabled ? "cursor-default" : "cursor-pointer hover:scale-110"} ${isSel ? "bg-slate-900 text-white border-black shadow-[2px_2px_0_rgba(0,0,0,0.85)]" : "bg-white text-slate-900 border-black"}`}>
            {n}
          </button>
        );
      })}
    </div>
  );
}

export function NpsStrip({ selected, onChange, disabled }) {
  const bucketColor = (n) => n <= 6 ? "#EF4444" : n <= 8 ? "#FFD23A" : "#3EB567";
  const handle = (n) => { if (!disabled && onChange) onChange([ String(n) ]); };
  return (
    <div>
      <div className="grid grid-cols-11 gap-1">
        {Array.from({ length: 11 }, (_, n) => {
          const isSel = selected != null && String(n) === String(selected);
          const color = bucketColor(n);
          return (
            <button key={n} type="button" disabled={disabled} onClick={() => handle(n)}
                    className={`aspect-square rounded border-[2px] text-sm font-bold transition-transform ${disabled ? "cursor-default" : "cursor-pointer hover:scale-110"}`}
                    style={{
                      background: isSel ? color : "#FFFFFF",
                      color: isSel ? "#FFFFFF" : "#1A1A1A",
                      borderColor: isSel ? "#1A1A1A" : color,
                      boxShadow: isSel ? "2px 2px 0 rgba(0,0,0,0.85)" : "none",
                    }}>
              {n}
            </button>
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] text-slate-500 mt-1 font-bold uppercase tracking-wider">
        <span>not at all likely</span>
        <span>extremely likely</span>
      </div>
    </div>
  );
}

// ─── Per-kind answer inputs ───────────────────────────────────────────

export function ShortInput({ value, onChange, disabled }) {
  return <input type="text" value={value} onChange={(e) => onChange?.(e.target.value)} disabled={disabled} autoFocus={!disabled} className="zz-input text-base" />;
}

export function LongInput({ value, onChange, disabled }) {
  return <textarea value={value} onChange={(e) => onChange?.(e.target.value)} rows={5} disabled={disabled} autoFocus={!disabled} className="zz-input text-base" />;
}

export function SingleInput({ options, value, onChange, disabled }) {
  // Auto-switch to a dropdown if there are many options (avoids a wall
  // of radios). Threshold lifted from the plan: 8+.
  if (options.length >= 8) {
    return (
      <select value={value[0] || ""} onChange={(e) => onChange?.([e.target.value])} disabled={disabled} className="zz-input text-base">
        <option value="">— pick one —</option>
        {options.map((o, i) => <option key={i} value={o}>{o}</option>)}
      </select>
    );
  }
  return (
    <div className="space-y-2">
      {options.map((o, i) => {
        const on = value[0] === o;
        return (
          <label key={i} className={`flex items-center gap-2 px-3 py-2 border-[2px] rounded ${disabled ? "cursor-default" : "cursor-pointer"} ${on ? "bg-slate-900 text-white border-black" : "bg-white border-black/20 hover:border-black"}`}>
            <input type="radio" checked={on} onChange={() => onChange?.([o])} disabled={disabled} className="h-4 w-4 accent-sspink" />
            {o}
          </label>
        );
      })}
    </div>
  );
}

export function MultiInput({ options, value, onChange, disabled }) {
  const toggle = (o) => onChange?.(value.includes(o) ? value.filter(x => x !== o) : [...value, o]);
  return (
    <div className="space-y-2">
      {options.map((o, i) => {
        const on = value.includes(o);
        return (
          <label key={i} className={`flex items-center gap-2 px-3 py-2 border-[2px] rounded ${disabled ? "cursor-default" : "cursor-pointer"} ${on ? "bg-slate-900 text-white border-black" : "bg-white border-black/20 hover:border-black"}`}>
            <input type="checkbox" checked={on} onChange={() => toggle(o)} disabled={disabled} className="h-4 w-4 accent-sspink" />
            {o}
          </label>
        );
      })}
    </div>
  );
}

// ─── Welcome / Question / Thanks ──────────────────────────────────────

export function WelcomeScreen({ survey, onStart, preview }) {
  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="p-6 sm:p-8 text-center">
        <h1 className="text-2xl sm:text-3xl font-black">{survey.welcome_title || survey.name}</h1>
        {survey.welcome_text && (
          <div className="text-base text-slate-700 mt-4 whitespace-pre-wrap">{survey.welcome_text}</div>
        )}
        <button onClick={onStart} disabled={preview} className={`zz-btn blue mt-6 ${preview ? "opacity-60 cursor-default" : ""}`}>start →</button>
      </motion.div>
    </AnimatePresence>
  );
}

export function QuestionScreen({ question, value, onAnswer, onNext, onBack, indexLabel, preview }) {
  // short / long hold a string, file holds a single uploaded URL string,
  // and the choice kinds (single / multi / nps / numeric) hold an array
  // of selected option strings. Checking Array.isArray for every non-text
  // kind would leave a required file question permanently blocked.
  const empty = Array.isArray(value)
    ? value.length === 0
    : !(value && value.toString().trim());
  const blocked = !preview && question.required && empty;

  return (
    <AnimatePresence>
      <motion.div key={question.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="p-6 sm:p-8">
        {indexLabel && <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{indexLabel}</p>}
        <h2 className="text-xl sm:text-2xl font-black mt-2">
          {question.prompt}
          {question.required
            ? <span className="text-ssred ml-1">*</span>
            : <span className="text-sm font-normal text-slate-400 ml-2 align-middle">optional</span>}
        </h2>
        {question.helper_text && (
          <p className="text-sm text-slate-500 mt-1">{question.helper_text}</p>
        )}

        <div className="mt-5">
          {question.kind === "short"   && <ShortInput   value={value || ""} onChange={onAnswer} disabled={preview} />}
          {question.kind === "long"    && <LongInput    value={value || ""} onChange={onAnswer} disabled={preview} />}
          {question.kind === "single"  && <SingleInput  options={question.options || []} value={value || []} onChange={onAnswer} disabled={preview} />}
          {question.kind === "multi"   && <MultiInput   options={question.options || []} value={value || []} onChange={onAnswer} disabled={preview} />}
          {question.kind === "nps"     && <NpsStrip     selected={Array.isArray(value) ? value[0] : null} onChange={onAnswer} disabled={preview} />}
          {question.kind === "numeric" && <NumericStrip selected={Array.isArray(value) ? value[0] : null} onChange={onAnswer} disabled={preview} />}
          {question.kind === "file"    && (preview
            ? <p className="text-xs text-slate-500 italic">(image uploader appears here)</p>
            : <ImageUploader initialImageUrl={value} setUrl={onAnswer} />)}
        </div>

        <div className="flex items-center justify-between mt-6">
          {onBack
            ? <button onClick={onBack} disabled={preview} className={`zz-link text-sm ${preview ? "opacity-60 cursor-default" : ""}`}>← back</button>
            : <span></span>}
          <button onClick={onNext} disabled={blocked || preview} className={`zz-btn blue ${(blocked || preview) ? "opacity-50 cursor-not-allowed" : ""}`}>
            continue →
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

export function TestimonialPick({ prompt, onPick, onSkip, preview }) {
  const click = (kind) => { if (!preview) onPick?.(kind); };
  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="p-6 sm:p-8 text-center">
        <p className="zz-hand text-2xl text-sspink leading-none">one more thing —</p>
        <h2 className="text-xl sm:text-2xl font-black mt-2">{prompt || "Would you mind leaving a testimonial?"}</h2>
        <div className="mt-5 flex flex-col gap-2">
          <button onClick={() => click("video")} disabled={preview} className={`zz-btn blue ${preview ? "opacity-60 cursor-default" : ""}`}>Upload a video</button>
          <button onClick={() => click("audio")} disabled={preview} className={`zz-btn sspurple ${preview ? "opacity-60 cursor-default" : ""}`}>Record audio</button>
          <button onClick={() => click("text")}  disabled={preview} className={`zz-btn ssorange ${preview ? "opacity-60 cursor-default" : ""}`}>Write text</button>
          <button onClick={preview ? undefined : onSkip} disabled={preview} className={`zz-link muted text-sm mt-2 ${preview ? "opacity-60 cursor-default" : ""}`}>skip this step →</button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// What the contact step asks for, in order. `key` is the Contact
// attribute; `setting` is the capform setting that governs it. Name has
// no setting, because a Contact is invalid without one, so it is always asked
// and always required.
const CONTACT_FIELDS = [
  { key: "avatar",       setting: "collect_avatar",       label: "Your avatar",  kind: "image" },
  { key: "name",         setting: null,                   label: "Your name",    placeholder: "Your name" },
  { key: "email",        setting: "collect_email",        label: "Email",        placeholder: "you@example.com", type: "email" },
  { key: "title",        setting: "collect_job_title",    label: "Job title",    placeholder: "Designer" },
  { key: "company_name", setting: "collect_company_name", label: "Company",      placeholder: "Acme Inc." },
  { key: "company_logo", setting: "collect_company_logo", label: "Company logo", kind: "image" },
  { key: "company_team", setting: "collect_company_team", label: "Department",   placeholder: "Marketing" },
];

// The contact step mirrors the capture form behind the testimonial step,
// because that form is what the resulting proof gets attributed through.
// Two cases don't consult it:
//
//   anonymous mode: a name and nothing else, by definition.
//   no proof:      nothing is being attributed, so asking for an avatar
//                    to file a survey answer is too much. Name and email,
//                    name required.
//
// "No proof" covers a survey with the testimonial step turned off, a
// respondent who was offered it and skipped, and a project with no
// capform at all. The step runs after the testimonial precisely so this
// is known rather than guessed from the survey's configuration.
export function contactCollectionFor(survey, minimal, hasProof) {
  if (minimal) return { name: "Required" };
  if (!hasProof || !survey.capform) {
    return { name: "Required", email: "Optional" };
  }
  const cf = survey.capform;
  return CONTACT_FIELDS.reduce((acc, f) => {
    acc[f.key] = f.setting ? (cf[f.setting] || "Do not collect") : "Required";
    return acc;
  }, {});
}

export function ContactScreen({ survey, contact, setContact, minimal, hasProof, onDone, preview }) {
  const update = (key, val) => setContact?.({ ...(contact || {}), [key]: val });
  const collection = contactCollectionFor(survey, minimal, hasProof);
  const fields = CONTACT_FIELDS.filter(f => {
    const setting = collection[f.key];
    return setting && setting !== "Do not collect";
  });

  const filled = (key) => Boolean((contact?.[key] || "").toString().trim());
  // Continue waits on every Required field, not just the name, because the
  // capture form asked for them for a reason.
  const missing = fields.filter(f => collection[f.key] === "Required" && !filled(f.key));
  const blocked = !preview && missing.length > 0;

  const subtitle = minimal
    ? "Just a name. Anonymous mode."
    : (missing.length === fields.length && fields.length > 1
        ? "A name is all we need; the rest is optional."
        : "So we know who to credit.");

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="p-6 sm:p-8">
        <h2 className="text-xl sm:text-2xl font-black">Who should we attribute this to?</h2>
        <p className="text-sm text-slate-600 mt-1">{subtitle}</p>

        <div className="mt-5 space-y-3">
          {fields.map(f => {
            const required = collection[f.key] === "Required";
            return (
              <div key={f.key}>
                <label className="zz-label">
                  {f.label}
                  {required && <span className="text-ssred ml-1">*</span>}
                  {!required && <span className="text-slate-400 font-normal ml-2">optional</span>}
                </label>
                {f.kind === "image"
                  ? (preview
                      ? <p className="text-xs text-slate-500 italic">(image uploader appears here)</p>
                      : <ImageUploader initialImageUrl={contact?.[f.key]} setUrl={(url) => update(f.key, url)} />)
                  : <input
                      type={f.type || "text"}
                      value={contact?.[f.key] || ""}
                      onChange={(e) => update(f.key, e.target.value)}
                      placeholder={f.placeholder}
                      disabled={preview}
                      autoFocus={!preview && f.key === "name"}
                      className="zz-input text-base"
                    />}
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-end mt-6">
          <button onClick={preview ? undefined : onDone} disabled={blocked || preview} className={`zz-btn blue ${(blocked || preview) ? "opacity-50 cursor-not-allowed" : ""}`}>continue →</button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// Landing page for a resume link that points at a finished response —
// reached from the "your spot is saved" email when the respondent went on
// to complete the survey before the mail was opened. Deliberately does NOT
// follow survey.thanks_url the way ThanksScreen does: a stale link should
// explain itself, not bounce the reader off to a post-submission
// destination they already saw.
export function AlreadyCompletedScreen({ survey }) {
  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="p-6 sm:p-8 text-center">
        <p className="zz-hand text-2xl text-ssgreen leading-none">all done —</p>
        <h2 className="text-2xl sm:text-3xl font-black mt-2">You already finished this one</h2>
        <p className="text-base text-slate-700 mt-3">
          Your answers to <strong>{survey.name}</strong> are already in. There's nothing left to fill out — you can close this tab.
        </p>
      </motion.div>
    </AnimatePresence>
  );
}

export function ThanksScreen({ survey, preview }) {
  // The runner redirects when thanks_url is set; the editor preview just
  // shows what the redirect notice would look like (no actual nav).
  useEffect(() => {
    if (preview) return undefined;
    if (survey.thanks_url) {
      window.location.href = survey.thanks_url;
    }
    return undefined;
  }, [preview, survey.thanks_url]);

  if (survey.thanks_url) {
    return (
      <div className="p-6 sm:p-8 text-center">
        <p className="text-sm text-slate-500">Redirecting…</p>
        <a href={preview ? undefined : survey.thanks_url} className="zz-link text-sm">{preview ? survey.thanks_url : "Continue manually"}</a>
      </div>
    );
  }
  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="p-6 sm:p-8 text-center">
        <p className="zz-hand text-2xl text-ssgreen leading-none">done —</p>
        <h2 className="text-2xl sm:text-3xl font-black mt-2">{survey.thanks_title || "Thanks!"}</h2>
        {survey.thanks_text && (
          <p className="text-base text-slate-700 mt-3 whitespace-pre-wrap">{survey.thanks_text}</p>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
